import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  DocumentIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  ViewColumnsIcon,
  Squares2X2Icon,
  ChevronDownIcon,
  CalendarIcon,
  UserIcon,
  EyeIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const DocumentCategories = () => {
  const { category } = useParams();
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const selectedCategory = category || '';
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [selectedAuthor, setSelectedAuthor] = useState('');

  useEffect(() => {
    // Simulate fetching data
    const fetchData = () => {
      const mockCategories = [
        {
          id: '1',
          name: 'HR Policies',
          slug: 'hr-policies',
          description: 'Human resources policies and procedures',
          documentCount: 24,
          icon: '👥',
          color: 'bg-blue-100 text-blue-800'
        },
        {
          id: '2',
          name: 'Medical Procedures',
          slug: 'medical-procedures',
          description: 'Clinical procedures and protocols',
          documentCount: 45,
          icon: '🏥',
          color: 'bg-green-100 text-green-800'
        },
        {
          id: '3',
          name: 'Safety Protocols',
          slug: 'safety-protocols',
          description: 'Safety guidelines and emergency procedures',
          documentCount: 18,
          icon: '🛡️',
          color: 'bg-red-100 text-red-800'
        },
        {
          id: '4',
          name: 'Training Materials',
          slug: 'training-materials',
          description: 'Educational and training resources',
          documentCount: 32,
          icon: '📚',
          color: 'bg-purple-100 text-purple-800'
        },
        {
          id: '5',
          name: 'Forms & Templates',
          slug: 'forms-templates',
          description: 'Standard forms and document templates',
          documentCount: 28,
          icon: '📋',
          color: 'bg-yellow-100 text-yellow-800'
        },
        {
          id: '6',
          name: 'IT Security',
          slug: 'it-security',
          description: 'Information technology and security policies',
          documentCount: 15,
          icon: '🔒',
          color: 'bg-indigo-100 text-indigo-800'
        },
        {
          id: '7',
          name: 'Quality Assurance',
          slug: 'quality-assurance',
          description: 'Quality control and assurance documents',
          documentCount: 21,
          icon: '✅',
          color: 'bg-emerald-100 text-emerald-800'
        },
        {
          id: '8',
          name: 'Financial Records',
          slug: 'financial-records',
          description: 'Financial procedures and documentation',
          documentCount: 12,
          icon: '💼',
          color: 'bg-gray-100 text-gray-800'
        }
      ];

      const mockDocuments = [
        {
          id: 1,
          title: 'Employee Handbook 2025',
          fileName: 'employee-handbook-2025.pdf',
          type: 'PDF',
          category: 'HR Policies',
          size: '2.4 MB',
          uploadDate: '2025-01-15',
          lastModified: '2025-01-20',
          author: 'Dr. Sarah Johnson',
          authorAvatar: '/images/avatars/sarah-johnson.jpg',
          description: 'Comprehensive employee handbook covering policies and procedures.',
          downloads: 145,
          views: 289,
          tags: ['HR', 'Policies', 'Guidelines']
        },
        {
          id: 2,
          title: 'COVID-19 Safety Protocol',
          fileName: 'covid-19-safety-protocol.doc',
          type: 'DOC',
          category: 'Safety Protocols',
          size: '1.8 MB',
          uploadDate: '2025-01-10',
          lastModified: '2025-01-12',
          author: 'Dr. Michael Chen',
          authorAvatar: '/images/avatars/michael-chen.jpg',
          description: 'Updated COVID-19 safety measures and protocols.',
          downloads: 98,
          views: 201,
          tags: ['Safety', 'COVID-19', 'Protocol']
        },
        {
          id: 3,
          title: 'CPR Training Manual',
          fileName: 'cpr-training-manual.pdf',
          type: 'PDF',
          category: 'Training Materials',
          size: '3.1 MB',
          uploadDate: '2025-01-08',
          lastModified: '2025-01-08',
          author: 'Lisa Rodriguez',
          authorAvatar: '/images/avatars/lisa-rodriguez.jpg',
          description: 'Comprehensive CPR training manual for all staff.',
          downloads: 76,
          views: 156,
          tags: ['Training', 'CPR', 'Emergency']
        },
        {
          id: 4,
          title: 'Patient Intake Form',
          fileName: 'patient-intake-form.pdf',
          type: 'PDF',
          category: 'Forms & Templates',
          size: '0.5 MB',
          uploadDate: '2025-01-05',
          lastModified: '2025-01-07',
          author: 'Jennifer Wu',
          authorAvatar: '/images/avatars/jennifer-wu.jpg',
          description: 'Standard patient intake form template.',
          downloads: 234,
          views: 445,
          tags: ['Forms', 'Patient', 'Intake']
        },
        {
          id: 5,
          title: 'IT Security Policy',
          fileName: 'it-security-policy.doc',
          type: 'DOC',
          category: 'IT Security',
          size: '1.2 MB',
          uploadDate: '2025-01-03',
          lastModified: '2025-01-05',
          author: 'David Kim',
          authorAvatar: '/images/avatars/david-kim.jpg',
          description: 'Information technology security policies and procedures.',
          downloads: 67,
          views: 134,
          tags: ['IT', 'Security', 'Policy']
        },
        {
          id: 6,
          title: 'Emergency Response Plan',
          fileName: 'emergency-response-plan.pdf',
          type: 'PDF',
          category: 'Safety Protocols',
          size: '2.8 MB',
          uploadDate: '2025-01-01',
          lastModified: '2025-01-02',
          author: 'Dr. Emily Davis',
          authorAvatar: '/images/avatars/emily-davis.jpg',
          description: 'Comprehensive emergency response procedures.',
          downloads: 89,
          views: 178,
          tags: ['Emergency', 'Safety', 'Response']
        }
      ];

      setCategories(mockCategories);
      setDocuments(mockDocuments);
      setLoading(false);
    };

    setTimeout(fetchData, 500);
  }, []);

  const filteredDocuments = documents.filter(document => {
    const matchesSearch = document.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         document.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         document.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = !selectedCategory || 
                           document.category.toLowerCase().replace(' ', '-') === selectedCategory;
    
    const matchesType = !selectedType || document.type === selectedType;
    
    const matchesAuthor = !selectedAuthor || document.author === selectedAuthor;

    return matchesSearch && matchesCategory && matchesType && matchesAuthor;
  });

  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'title':
        comparison = a.title.localeCompare(b.title);
        break;
      case 'author':
        comparison = a.author.localeCompare(b.author);
        break;
      case 'size':
        comparison = parseFloat(a.size) - parseFloat(b.size);
        break;
      case 'downloads':
        comparison = a.downloads - b.downloads;
        break;
      default: // date
        comparison = new Date(a.lastModified) - new Date(b.lastModified);
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const uniqueTypes = [...new Set(documents.map(doc => doc.type))];
  const uniqueAuthors = [...new Set(documents.map(doc => doc.author))];

  const currentCategory = categories.find(cat => cat.slug === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            {/* Breadcrumb */}
            <nav className="flex mb-4" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-3">
                <li className="inline-flex items-center">
                  <Link to="/documents" className="text-gray-700 hover:text-gray-900">
                    Documents
                  </Link>
                </li>
                {selectedCategory && (
                  <li>
                    <div className="flex items-center">
                      <span className="mx-2 text-gray-400">/</span>
                      <span className="text-gray-500">Categories</span>
                    </div>
                  </li>
                )}
                {currentCategory && (
                  <li>
                    <div className="flex items-center">
                      <span className="mx-2 text-gray-400">/</span>
                      <span className="text-gray-500">{currentCategory.name}</span>
                    </div>
                  </li>
                )}
              </ol>
            </nav>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {currentCategory ? currentCategory.name : 'Document Categories'}
                </h1>
                <p className="mt-2 text-sm text-gray-700">
                  {currentCategory 
                    ? `${currentCategory.description} • ${sortedDocuments.length} documents`
                    : `Browse documents by category • ${categories.length} categories available`
                  }
                </p>
              </div>
              <div className="mt-4 sm:mt-0">
                <Link
                  to="/documents/upload"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <DocumentIcon className="-ml-1 mr-2 h-4 w-4" />
                  Upload Document
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Grid (if no specific category selected) */}
      {!selectedCategory && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/documents/categories/${category.slug}`}
                className="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200"
              >
                <div className="p-6">
                  <div className="flex items-center">
                    <div className="text-2xl mr-3">{category.icon}</div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">{category.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{category.description}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${category.color}`}>
                      {category.documentCount} documents
                    </span>
                    <FolderIcon className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Documents List (if category selected) */}
      {selectedCategory && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search and Filters */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              {/* Search */}
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Search documents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center space-x-4">
                {/* Filters Toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
                  Filters
                </button>

                {/* Sort */}
                <div className="relative">
                  <select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split('-');
                      setSortBy(field);
                      setSortOrder(order);
                    }}
                    className="block appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm leading-5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="date-desc">Newest First</option>
                    <option value="date-asc">Oldest First</option>
                    <option value="title-asc">Title A-Z</option>
                    <option value="title-desc">Title Z-A</option>
                    <option value="author-asc">Author A-Z</option>
                    <option value="downloads-desc">Most Downloaded</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <ChevronDownIcon className="h-4 w-4" />
                  </div>
                </div>

                {/* View Toggle */}
                <div className="flex rounded-md shadow-sm">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`${
                      viewMode === 'grid'
                        ? 'bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:text-gray-700'
                    } relative inline-flex items-center px-3 py-2 rounded-l-md border text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
                  >
                    <Squares2X2Icon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`${
                      viewMode === 'list'
                        ? 'bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:text-gray-700'
                    } relative inline-flex items-center px-3 py-2 -ml-px rounded-r-md border text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
                  >
                    <ViewColumnsIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      File Type
                    </label>
                    <select
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Types</option>
                      {uniqueTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Author
                    </label>
                    <select
                      value={selectedAuthor}
                      onChange={(e) => setSelectedAuthor(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Authors</option>
                      {uniqueAuthors.map(author => (
                        <option key={author} value={author}>{author}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={() => {
                        setSelectedType('');
                        setSelectedAuthor('');
                        setSearchTerm('');
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Documents Grid/List */}
          {sortedDocuments.length === 0 ? (
            <div className="text-center py-12">
              <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No documents found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search or filter criteria.
              </p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedDocuments.map((document) => (
                <div key={document.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200">
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center">
                        <DocumentIcon className="h-8 w-8 text-blue-500" />
                        <div className="ml-3 flex-1">
                          <Link
                            to={`/documents/${document.id}`}
                            className="text-lg font-medium text-gray-900 hover:text-blue-600"
                          >
                            {document.title}
                          </Link>
                          <p className="text-sm text-gray-500">{document.fileName}</p>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {document.type}
                      </span>
                    </div>
                    
                    <p className="mt-3 text-sm text-gray-600 line-clamp-2">
                      {document.description}
                    </p>
                    
                    <div className="mt-4 flex items-center text-sm text-gray-500">
                      <UserIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                      {document.author}
                    </div>
                    
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                      {document.lastModified}
                    </div>
                    
                    <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <EyeIcon className="h-4 w-4 mr-1" />
                          {document.views}
                        </div>
                        <div className="flex items-center">
                          <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                          {document.downloads}
                        </div>
                      </div>
                      <span className="text-xs">{document.size}</span>
                    </div>
                    
                    {document.tags && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {document.tags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="divide-y divide-gray-200">
                {sortedDocuments.map((document) => (
                  <div key={document.id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center flex-1 min-w-0">
                        <DocumentIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
                        <div className="ml-4 flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <Link
                              to={`/documents/${document.id}`}
                              className="text-lg font-medium text-gray-900 hover:text-blue-600 truncate"
                            >
                              {document.title}
                            </Link>
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {document.type}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{document.description}</p>
                          <div className="mt-2 flex items-center space-x-6 text-sm text-gray-500">
                            <div className="flex items-center">
                              <UserIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                              {document.author}
                            </div>
                            <div className="flex items-center">
                              <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                              {document.lastModified}
                            </div>
                            <div className="flex items-center">
                              <EyeIcon className="h-4 w-4 mr-1" />
                              {document.views}
                            </div>
                            <div className="flex items-center">
                              <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                              {document.downloads}
                            </div>
                            <span>{document.size}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentCategories;
